import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Form, Button, message } from "antd";
import {
    GoogleReCaptchaProvider,
    GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import useGet from "../../../services/useGet";
import usePost from "../../../services/usePost";
import sidebarImage from "../../../assets/login-sidebar.png";
import { showUserNotification } from "../../../utils/functions";
import SOURCES from "../../../utils/constants";
import CommonForm from "../../global/form";
import Spinner from "../../global/spinner";
import CheckoutForm from "../../stripe/CheckoutForm"; // Reusing existing checkout form
import useDebounce from "../../../utils/customHooks";
import CommonInput from "../../global/input";
import CommonSelect from "../../global/select";

const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY_LIVE;

const EnhancedSignup = () => {
    const { DISCOUNT_TRIAL } = SOURCES;
    const history = useHistory();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const stripe = useStripe();
    const elements = useElements();

    // Query Params
    const queryParams = new URLSearchParams(window.location.search);
    const plan = queryParams.get("plan");
    const trial = queryParams.get("trial") === null ? "n" : queryParams.get("trial");
    const paymentDuration = queryParams.get("paymentDuration") === null ? "monthly" : queryParams.get("paymentDuration");
    const referral = queryParams.get("ref") === null ? "" : queryParams.get("ref");
    const accessCodePrivatePlan = queryParams.get("accessPrivatePlanCode") === null ? "" : queryParams.get("accessPrivatePlanCode");

    console.log(plan)
    // State
    const [loading, setLoading] = useState(false);
    const [coupon, setCoupon] = useState("");
    const [couponId, setCouponId] = useState("");
    const [couponTrial, setCouponTrial] = useState(false);
    const [captchaToken, setCaptchaToken] = useState();
    const [paymentPlanId, setPaymentPlanId] = useState("");
    const [paymentPlanDescription, setPaymentPlanDescription] = useState("");
    const [paymentStripePlanId, setPaymentStripePlanId] = useState("-1");
    const [paymentPlanCode, setPaymentPlanCode] = useState("");

    const debouncedSearchTerm = useDebounce(coupon, 500);

    // API Hooks
    const { callApi: getPaymentPlans, data: paymentPlans } = useGet({ endpoint: `payment/plans` });
    const { callApi: checkUser } = usePost({ endpoint: `/user/check` });
    const { callApi: doSubscription } = usePost({ endpoint: `/payment/subscription` });
    const { callApi: doSignup } = usePost({ endpoint: `/user/signup` });
    const { callApi: doPaymentVerify } = usePost({ endpoint: `user/payment/verified` });
    const { callApi: validateCoupon } = usePost({ endpoint: `/coupon/validate-coupon` });

    useEffect(() => {
        getPaymentPlans();
    }, []);

    useEffect(() => {
        if (plan) getPaymentPlanDetail(plan);
    }, [plan, paymentPlans]);

    console.log(paymentPlans);

    useEffect(() => {
        if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) checkTrialCoupon();
    }, [debouncedSearchTerm]);

    const getPaymentPlanDetail = async (planId) => {
        setPaymentPlanId(planId);
        form.setFieldsValue({ paymentPlanId: planId });
        setPaymentPlanDescription("");
        if (paymentPlans) {
            console.log(paymentPlans);
            const selectedPlan = paymentPlans.find(p => p._id === planId);
            if (selectedPlan) {
                setPaymentStripePlanId(selectedPlan.stripePlanId);
                setPaymentPlanDescription(selectedPlan.planDetail);
                setPaymentPlanCode(selectedPlan.planCode);
            }
        }
    };

    const checkTrialCoupon = async () => {
        setCouponTrial(false);
        const couponCheck = await validateCoupon({ coupon });
        if (couponCheck) {
            setCouponId(couponCheck._id);
            if (couponCheck?.couponType === DISCOUNT_TRIAL) setCouponTrial(true);
        }
    };

    const signupProcess = async (values, paymentStatus = false, isTrial = "n") => {
        try {
            const result = await doSignup({
                email: values.email,
                password: values.password,
                confirmPassword: values.confirmPassword,
                name: values.name,
                phone: values.phone,
                company: values.company,
                paymentStatus,
                paymentPlanId: values.paymentPlanId,
                captchaToken,
                isTrial: trial === SOURCES.TRIAL_TYPES.FIFTEEN_DAYS_TRIAL || trial === SOURCES.TRIAL_TYPES.THIRTY_DAYS_TRIAL ? "y" : "n",
                couponId,
                referral,
            });
            return result;
        } catch (err) {
            if (err.response?.data?.data?.error) {
                dispatch(showUserNotification(err.response.data.data.error, "danger"));
            }
            return null;
        }
    };

    const paymentStatusFn = async (email, verificationCode, stripeSubscriptionId) => {
        try {
            await doPaymentVerify({ email, verificationCode, stripeSubscriptionId });
        } catch (err) {
            if (err.response?.data?.data?.error) {
                dispatch(showUserNotification(err.response.data.data.error, "danger"));
            }
        }
    };

    const handleFinish = async (values) => {
        setLoading(true);
        try {
            // Check User Existence
            const userCheck = await checkUser({ email: values.email }, null, true, false);
            if (userCheck?.status === 403) {
                setLoading(false);
                message.error(userCheck.data.error);
                return;
            }

            // Coupon / Trial Logic
            if (coupon) {
                const couponCheck = await validateCoupon({ coupon });
                if (!couponCheck) {
                    setCoupon("");
                    setCouponId(null);
                    setLoading(false);
                    return;
                } else if (couponCheck?.couponType === DISCOUNT_TRIAL) {
                    const signupResult = await signupProcess(values, true, "y");
                    if (!signupResult) {
                        setLoading(false);
                        return;
                    }
                    message.success("Please check your email");
                    setTimeout(() => history.push("/"), 2000);
                    return;
                }
            }

            console.log(paymentStripePlanId);
            // Payment Logic
            if (paymentStripePlanId === "-1") {
                setLoading(false);
                message.error("Choose Plan");
                return;
            }

            if (paymentStripePlanId) {
                if (!stripe || !elements) {
                    setLoading(false);
                    return;
                }

                let resultStripe = null;
                if (trial !== SOURCES.TRIAL_TYPES.FIFTEEN_DAYS_TRIAL && trial !== SOURCES.TRIAL_TYPES.THIRTY_DAYS_TRIAL) {
                    resultStripe = await stripe.createPaymentMethod({
                        type: "card",
                        card: elements.getElement(CardElement),
                        billing_details: { email: values.email },
                    });
                }

                if (resultStripe && resultStripe.error) {
                    setLoading(false);
                    message.error(resultStripe.error.message);
                } else {
                    const signupResult = await signupProcess(values, false, trial);
                    if (!signupResult) {
                        setLoading(false);
                        return;
                    }
                    const { verificationCode } = signupResult;

                    try {
                        const res = await doSubscription({
                            payment_method: resultStripe ? resultStripe.paymentMethod.id : null,
                            email: values.email,
                            name: values.name,
                            stripePlanId: paymentStripePlanId,
                            coupon,
                            trial,
                            referral,
                            paymentDuration,
                        });
                        const { client_secret, status, subscription_id } = res;

                        if (status === "requires_action") {
                            const confirmResult = await stripe.confirmCardPayment(client_secret);
                            if (confirmResult.error) {
                                message.error(confirmResult.error.message);
                            } else {
                                await paymentStatusFn(values.email, verificationCode, subscription_id);
                                message.success("Please check your email");
                                setTimeout(() => history.push("/"), 2000);
                            }
                        } else {
                            await paymentStatusFn(values.email, verificationCode, subscription_id);
                            message.success("Please check your email");
                            setTimeout(() => history.push("/"), 2000);
                        }
                    } catch (errSub) {
                        setLoading(false);
                        message.error("Subscription failed");
                    }
                }
            } else {
                // Pay As You Go
                const signupResult = await signupProcess(values, true);
                setLoading(false);
                if (!signupResult) return;
                message.success("Please check your email");
                setTimeout(() => history.push("/"), 2000);
            }

        } catch (err) {
            setLoading(false);
            message.error("Unexpected error occurred please try again");
        } finally { }
    };

    const planOptions = (Array.isArray(paymentPlans) ? paymentPlans : [])
        ?.filter(plan => plan.planCode !== SOURCES.PAYMENT_PLAN_CODE.P_1)
        .filter(plan => plan.isPrivatePlan === "n" || accessCodePrivatePlan === plan.accessCodePrivatePlan)
        .map(plan => ({
            label: plan.planName,
            value: plan._id,
        })) || [];

    const formData = [
        {
            label: "Full Name",
            name: "name",
            type: "text",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Full name",
            span: 12,
            message: "Name is required",
        },
        {
            label: "Company Name",
            name: "company",
            type: "text",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Company name",
            span: 12,
            message: "Company is required",
        },
        {
            label: "Phone Number",
            name: "phone",
            type: "text",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Phone number",
            span: 12,
            message: "Phone is required",
        },
        {
            label: "Email",
            name: "email",
            type: "email",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Email address",
            span: 12,
            message: "Email is required",
        },
        {
            label: "Password",
            name: "password",
            type: "password",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Password",
            span: 12,
            message: "Password is required",
        },
        {
            label: "Confirm Password",
            name: "confirmPassword",
            type: "password",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Confirm password",
            span: 12,
            message: "Confirm Password is required",
            dependencies: ['password'],
            rules: [
                ({ getFieldValue }) => ({
                    validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords that you entered do not match!'));
                    },
                }),
            ],
        },
        {
            label: "Plan",
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <div className="space-y-1">
                    <Form.Item
                        name="paymentPlanId"
                        rules={[{ required: true, message: "Plan is required" }]}
                        noStyle
                    >
                        <CommonSelect
                            placeholder="Choose Plan"
                            options={planOptions}
                            onChange={(val) => getPaymentPlanDetail(val)}
                        />
                    </Form.Item>
                    {
                        (paymentPlanCode && paymentPlanCode !== SOURCES.PAYMENT_PLAN_CODE.P_1) ? (
                            <div className="mb-1 text-sm text-gray-600">
                                {
                                    (trial === SOURCES.TRIAL_TYPES.FIFTEEN_DAYS_TRIAL || trial === SOURCES.TRIAL_TYPES.THIRTY_DAYS_TRIAL) ?
                                        <span>Your free trial will expire in {trial} days.</span> :
                                        <span>{paymentPlanDescription}</span>
                                }
                            </div>
                        ) : null
                    }
                </div>
            )
        },
        // Trial / Plan Description Text

        // Coupon Input
        (paymentPlanCode &&
            paymentPlanCode !== SOURCES.PAYMENT_PLAN_CODE.P_1 &&
            trial !== SOURCES.TRIAL_TYPES.FIFTEEN_DAYS_TRIAL &&
            trial !== SOURCES.TRIAL_TYPES.THIRTY_DAYS_TRIAL) ? {
            label: "Coupon",
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,

            component: (
                // <Input
                //     className="h-10 w-full border hover:!border-primary focus:!border-primary rounded-lg bg-gray-50/30"
                //     placeholder="Enter coupon If any..."
                //     value={coupon}
                //     onChange={(e) => setCoupon(e.target.value)}
                // />
                <CommonInput
                    label="Coupon"
                    name="coupon"
                    type="text"
                    placeholder="Enter coupon If any..."
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value)}
                />
            )
        } : null,
        // Stripe Form
        (!couponTrial && paymentPlanCode !== SOURCES.PAYMENT_PLAN_CODE.P_1 &&
            trial !== SOURCES.TRIAL_TYPES.FIFTEEN_DAYS_TRIAL &&
            trial !== SOURCES.TRIAL_TYPES.THIRTY_DAYS_TRIAL) ? {
            label: "Card Details",
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                // <div className="border p-4 rounded-md bg-gray-50">
                <CheckoutForm />
                // </div>
            )
        } : null
    ].filter(Boolean);

    return (
        <div className="flex min-h-screen">
            <GoogleReCaptchaProvider reCaptchaKey={siteKey} >
                <GoogleReCaptcha onVerify={(token) => setCaptchaToken(token)} />
            </GoogleReCaptchaProvider>

            {/* Left Side - Image & Slogan */}
            <div className="hidden lg:flex w-1/2 relative bg-gray-900 items-center justify-center overflow-hidden max-h-screen">
                <img
                    src={sidebarImage}
                    alt="Signup Background"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Logo on Sidebar */}
                <div className="absolute top-8 left-8 z-30">
                    <h1 className="text-4xl font-bold tracking-tighter m-0">
                        <span className="text-primary">Leads</span>
                        <span className="text-white">Import</span>
                    </h1>
                </div>
            </div>

            {/* Right Side - Signup Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center bg-white py-8 pt-24 overflow-y-auto max-h-screen ">
                <div className="w-full max-w-2xl space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="lg:!text-center text-left flex items-center lg:justify-center justify-between mb-6">
                        <div className="lg:!text-center text-left">
                            <h2 className="text-3xl font-bold text-gray-900">Get Started</h2>
                            <p className="mt-2 text-gray-500">Create your account to start importing leads</p>
                        </div>
                        <div className="lg:hidden mb-6 ">
                            <h1 className="text-3xl font-bold tracking-tighter m-0 ">
                                <span className="text-primary">Leads</span>
                                <span className="text-gray-900">Import</span>
                            </h1>
                        </div>
                    </div>

                    <div className="bg-white">
                        <CommonForm
                            form={form}
                            data={formData}
                            onFinish={handleFinish}
                            isInternalSubmitt={false}
                        />

                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            className="w-full h-12 text-base font-bold rounded-xl shadow-lg shadow-primary/25 !bg-primary hover:!bg-primary/90 border-none mt-2 gap-x-2"
                        >
                            {loading && <Spinner className="!border-white" />} Sign Up
                        </Button>

                        <p className="text-center mt-6 text-gray-600">
                            Already have an account?{" "}
                            <Link to="/" className="font-bold text-primary hover:text-primary/80">
                                Sign In
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedSignup;
